/* De locatiemonitor op de projectpagina: een donker blok met wat de installatie
 * nu doet, het verloop van vandaag per uur tegen contract en sturingsdrempel, en
 * de dagtotalen. Alleen als het project een actieve EMS-locatie heeft.
 *
 * Geen meting is geen waarde (ADR 0014): dan staat er een streep, geen nul. Een
 * stand ouder dan een halfuur krijgt zijn eigen tijdstip. */

import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import type { Monitor, MonitorWaarde } from '../data/monitor';
import { getal, tijd } from './opmaak';

const KLEUR = {
  verbruik: '#f8fafc',
  net: '#60a5fa',
  zon: '#fbbf24',
  batterij: '#34d399',
  contract: '#64748b',
  drempel: '#fb923c',
};

function Tegel({ label, waarde, eenheid, kleur, oud, zone, voet }: {
  label: string;
  waarde: string;
  eenheid: string;
  kleur: string;
  oud?: string | null;
  zone: string;
  voet?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1 rounded-[10px] bg-[#111a2e] px-3.5 py-3">
      <span className="text-xs text-slate-400">{label}</span>
      <span className="text-[28px] font-semibold leading-tight tabular-nums" style={{ color: kleur }}>
        {waarde}
        {waarde !== '—' && <span className="ml-1 text-sm font-medium text-slate-400">{eenheid}</span>}
      </span>
      {oud && <span className="text-[11px] text-slate-400">meting van {tijd(oud, zone)}</span>}
      {voet}
    </div>
  );
}

const oudVan = (w: MonitorWaarde) => (w.oud ? w.tijdstip : null);

/** Uurgrafiek als SVG die meeschaalt met de breedte (viewBox 800×210). */
export function Daggrafiek({ monitor }: { monitor: Monitor }) {
  const L = 36, W = 760, H = 168, TOP = 10;
  const alle = [...monitor.uren.verbruik, ...monitor.uren.net, ...monitor.uren.zon].filter((v): v is number => v !== null);
  const MAX = Math.max(50, monitor.netContractKw ?? 0, ...alle) * 1.08;
  const x = (u: number) => L + ((u + 0.5) / 24) * W;
  const y = (v: number) => TOP + H - (Math.max(0, v) / MAX) * H;
  const lijn = (reeks: (number | null)[]) => {
    let d = '';
    let pen = false;
    reeks.forEach((v, u) => {
      if (v === null) { pen = false; return; }
      d += `${pen ? 'L' : 'M'}${x(u).toFixed(1)},${y(v).toFixed(1)} `;
      pen = true;
    });
    return d.trim();
  };
  const vlak = (reeks: (number | null)[]) => {
    const punten = reeks.map((v, u) => [u, v] as const).filter(([, v]) => v !== null) as [number, number][];
    if (punten.length < 2) return '';
    return `M${x(punten[0]![0])},${y(0)} ${punten.map(([u, v]) => `L${x(u).toFixed(1)},${y(v).toFixed(1)}`).join(' ')} L${x(punten.at(-1)![0])},${y(0)} Z`;
  };
  const piek = monitor.uren.net.reduce<{ u: number; v: number } | null>((p, v, u) => (v !== null && (!p || v > p.v) ? { u, v } : p), null);
  const laatsteUur = monitor.uren.verbruik.reduce<number | null>((p, v, u) => (v !== null ? u : p), null);
  const stappen = MAX > 400 ? 200 : 100;
  const raster = Array.from({ length: Math.floor(MAX / stappen) + 1 }, (_, i) => i * stappen);

  return (
    <svg viewBox="0 0 800 206" className="h-auto w-full" role="img" aria-label="Vermogen vandaag per uur: verbruik, net en zon">
      {raster.map((v) => (
        <g key={v}>
          <line x1={L} x2={L + W} y1={y(v)} y2={y(v)} stroke={v === 0 ? '#1e293b' : '#172035'} strokeWidth={1} />
          <text x={L - 8} y={y(v) + 4} textAnchor="end" fontSize={11} fill="#94a3b8">{v}</text>
        </g>
      ))}
      {monitor.netContractKw != null && (
        <g>
          <line x1={L} x2={L + W} y1={y(monitor.netContractKw)} y2={y(monitor.netContractKw)} stroke={KLEUR.contract} strokeWidth={1.2} strokeDasharray="5 4" />
          <text x={L + W - 4} y={y(monitor.netContractKw) - 5} textAnchor="end" fontSize={11} fill="#cbd5e1">contract {monitor.netContractKw} kW</text>
        </g>
      )}
      {monitor.sturingsdrempelKw != null && (
        <g>
          <line x1={L} x2={L + W} y1={y(monitor.sturingsdrempelKw)} y2={y(monitor.sturingsdrempelKw)} stroke={KLEUR.drempel} strokeWidth={1.2} strokeDasharray="2 3" />
          <text x={L + W - 4} y={y(monitor.sturingsdrempelKw) - 5} textAnchor="end" fontSize={11} fill="#fdba74">sturing {monitor.sturingsdrempelKw} kW</text>
        </g>
      )}
      <path d={vlak(monitor.uren.zon)} fill={KLEUR.zon} fillOpacity={0.22} stroke={KLEUR.zon} strokeWidth={1.5} />
      <path d={lijn(monitor.uren.net)} fill="none" stroke={KLEUR.net} strokeWidth={2} />
      <path d={lijn(monitor.uren.verbruik)} fill="none" stroke={KLEUR.verbruik} strokeWidth={2.2} />
      {laatsteUur !== null && <circle cx={x(laatsteUur)} cy={y(monitor.uren.verbruik[laatsteUur]!)} r={4} fill={KLEUR.verbruik} />}
      {piek && piek.v > 0 && (
        <g>
          <circle cx={x(piek.u)} cy={y(piek.v)} r={4} fill="#0b1220" stroke={KLEUR.net} strokeWidth={2} />
          <text x={x(piek.u)} y={y(piek.v) - 10} textAnchor="middle" fontSize={11} fontWeight={600} fill="#93c5fd">
            net {getal(piek.v)} kW · {String(piek.u).padStart(2, '0')}:00
          </text>
        </g>
      )}
      {[0, 6, 12, 18, 24].map((u) => (
        <text key={u} x={L + (u / 24) * W} y={202} textAnchor={u === 24 ? 'end' : 'middle'} fontSize={11} fill="#94a3b8">
          {String(u).padStart(2, '0')}
        </text>
      ))}
    </svg>
  );
}

export function MonitorBlok({ monitor }: { monitor: Monitor }) {
  const { nu, vandaag, tijdzone: zone } = monitor;
  const net = nu.net.kw;
  const volledig = monitor.siteId ? `/crm/sites/${monitor.siteId}` : `/crm/ems/${monitor.locatieId}`;
  const heeftUren = [...monitor.uren.verbruik, ...monitor.uren.net, ...monitor.uren.zon].some((v) => v !== null);

  return (
    <section id="monitor" aria-labelledby="pagina-monitor" className="scroll-mt-14 rounded-[14px] bg-[#0b1220] px-5 py-5 text-slate-200 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <h2 id="pagina-monitor" className="text-[15px] font-semibold text-slate-50">Locatiemonitor · {monitor.naam}</h2>
          <span className="flex items-center gap-1.5 text-xs text-slate-400">
            <span className={`h-[7px] w-[7px] rounded-full ${nu.laatste ? 'bg-emerald-400' : 'bg-slate-500'}`} aria-hidden />
            {nu.laatste ? `laatste meting ${tijd(nu.laatste, zone)}` : 'nog geen meting'}
          </span>
        </div>
        <Link to={volledig} className="flex items-center gap-1 text-[13px] text-blue-300 hover:text-blue-200">
          Volledige monitor
          <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
        </Link>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tegel label="Verbruik pand" waarde={getal(nu.verbruik.kw)} eenheid="kW" kleur={KLEUR.verbruik} oud={oudVan(nu.verbruik)} zone={zone} />
        <Tegel
          label={net !== null && net < 0 ? 'Naar het net' : 'Uit het net'}
          waarde={net === null ? '—' : getal(Math.abs(net))}
          eenheid="kW"
          kleur="#93c5fd"
          oud={oudVan(nu.net)}
          zone={zone}
        />
        <Tegel label="Zon" waarde={getal(nu.zon.kw)} eenheid="kW" kleur="#fcd34d" oud={oudVan(nu.zon)} zone={zone} />
        <Tegel
          label={nu.batterij.capaciteitKwh ? `Batterij · ${getal(nu.batterij.kwh)} / ${getal(nu.batterij.capaciteitKwh)} kWh` : 'Batterij'}
          waarde={nu.batterij.procent === null ? '—' : getal(nu.batterij.procent)}
          eenheid="%"
          kleur="#6ee7b7"
          oud={nu.batterij.oud ? nu.batterij.tijdstip : null}
          zone={zone}
          voet={nu.batterij.procent !== null && (
            <span className="block h-1 overflow-hidden rounded bg-slate-800" aria-hidden>
              <span className="block h-1 bg-emerald-400" style={{ width: `${Math.min(100, Math.max(0, nu.batterij.procent))}%` }} />
            </span>
          )}
        />
      </div>

      <div className="mt-4">
        {heeftUren ? (
          <Daggrafiek monitor={monitor} />
        ) : (
          <p className="rounded-lg border border-slate-800 px-4 py-6 text-center text-sm text-slate-400">Vandaag nog geen kwartierwaarden binnen.</p>
        )}
      </div>

      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 border-t border-slate-800 pt-3 text-[13px] text-slate-300">
        <span className="text-slate-400">Vandaag</span>
        <span>Verbruik <strong className="text-slate-50">{getal(vandaag.verbruikKwh)} kWh</strong></span>
        <span>Zon <strong className="text-amber-300">{getal(vandaag.zonKwh)} kWh</strong></span>
        <span>Uit net <strong className="text-blue-300">{getal(vandaag.netAfnameKwh)} kWh</strong></span>
        <span>Terug <strong className="text-slate-50">{getal(vandaag.terugleveringKwh)} kWh</strong></span>
        <span>Uit batterij <strong className="text-emerald-300">{getal(vandaag.uitBatterijKwh)} kWh</strong></span>
      </div>

      {monitor.installatie.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-2 text-xs text-slate-300" aria-label="Installatie">
          {monitor.installatie.map((i) => (
            <li key={i.label} className="rounded-full border border-slate-700 px-2.5 py-1">{i.label}</li>
          ))}
        </ul>
      )}
    </section>
  );
}
